import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '~/ui/core/context-menu/ContextMenu';
import { IconChatBubble7 } from '~/ui/core/icons';

import {
    type CommentDocumentState,
    CommentsClassificationPublisher,
    ContainedSelectionCommentButton,
    commentHighlightStateClasses,
    useCommentableAnchor,
} from '../../../comments';
import type { ProfileTask } from '../model/profile-task';
import { profileTaskRichTextToPlainText } from '../profile-task-rich-text';
import { profileTaskTranslationKeys } from '../profile-task-translations';

type StepPartKind = 'workflow-step-description' | 'workflow-step-title';

/**
 * One half of a workflow step — its title or its description — as its own comment anchor, which is
 * how the work-task comments api addresses them.
 */
export const TaskWorkflowStepPartCommentable: FunctionComponent<
    PropsWithChildren<{
        readonly itemId: string;
        readonly kind: StepPartKind;
        readonly newThreadCreationDisabled: boolean;
        /** The stored text this part renders; the api validates the anchor against exactly it. */
        readonly sourceText: string;
        readonly stepIndex: number;
    }>
> = ({ children, itemId, kind, newThreadCreationDisabled, sourceText, stepIndex }) => {
    const translate = useTranslate();
    const rowRef = useRef<HTMLDivElement>(null);
    const anchor = useCommentableAnchor({ itemId, kind, stepIndex });
    const hasComment = !is.undefined(anchor.commentId);
    // Workflow-step comments carry the whole part as their quote: the api rejects an anchorless
    // comment, and the row is commented on as a unit rather than by selection.
    const startPartComment = (anchorRect?: DOMRect): void => {
        const exact = sourceText.trim();
        if (exact.length === 0) return;
        const start = sourceText.indexOf(exact);
        anchor.startComment(rowRef.current, {
            ...(is.undefined(anchorRect) ? {} : { anchorRect }),
            anchor: {
                positionSelector: { start, end: start + exact.length },
                quoteSelector: { exact },
                status: 'anchored',
            },
        });
    };

    return (
        <>
            <ContextMenu>
                <ContextMenuTrigger>
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: the part only becomes a button once it carries a thread. */}
                    <div
                        ref={rowRef}
                        className={clsx(
                            '-mx-xs rounded-lg px-xs transition-colors duration-200',
                            commentHighlightStateClasses[anchor.state],
                        )}
                        onClick={
                            hasComment
                                ? (event) => {
                                      // The enclosing workflow card may be a click target of its own.
                                      event.stopPropagation();
                                      anchor.openThread();
                                  }
                                : undefined
                        }
                        onKeyDown={
                            hasComment
                                ? (event) => {
                                      if (event.target !== event.currentTarget || event.repeat) return;
                                      if (event.key !== 'Enter' && event.key !== ' ') return;
                                      event.preventDefault();
                                      anchor.openThread();
                                  }
                                : undefined
                        }
                        role={hasComment ? 'button' : undefined}
                        tabIndex={hasComment ? 0 : undefined}
                        {...anchor.attrs}
                    >
                        {children}
                    </div>
                </ContextMenuTrigger>
                {newThreadCreationDisabled ? null : (
                    <ContextMenuContent>
                        <ContextMenuItem
                            onSelect={() => {
                                startPartComment();
                            }}
                        >
                            <IconChatBubble7 size={16} />
                            {translate('pages.job-architecture-v2.comments.selection-button.label')}
                        </ContextMenuItem>
                    </ContextMenuContent>
                )}
            </ContextMenu>
            <ContainedSelectionCommentButton
                containerRef={rowRef}
                disabled={newThreadCreationDisabled}
                onComment={(anchorRect) => startPartComment(anchorRect)}
            />
        </>
    );
};

export const ProfileTaskCommentsPublisher: FunctionComponent<{
    readonly task: ProfileTask;
}> = ({ task }) => {
    const translate = useTranslate();
    const documentState = useMemo<CommentDocumentState>(() => {
        const sections = [
            {
                id: 'description',
                text: profileTaskRichTextToPlainText(task.description),
                title: translate(profileTaskTranslationKeys.description),
            },
        ].flatMap(({ id, text, title }) => (text === undefined || text.length === 0 ? [] : [{ id, text, title }]));

        return {
            sections,
            skills: task.requiredSkills.map(({ skillId, title }) => ({ id: skillId, title })),
            workflowSteps: task.workflowSteps.map((step) => ({
                id: step.workflowStepId,
                title: step.title,
            })),
        };
    }, [task, translate]);

    return <CommentsClassificationPublisher documentState={documentState} />;
};
